import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
    Grid,
    Button,
    Box,
    InputAdornment,
    IconButton,
    Stack,
    Typography,
    Divider,
    TextField,
    Avatar,
    Card,
    Chip,
    Tabs,
    Tab,
    Paper
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { LoadingButton } from '@mui/lab';
import { Formik } from 'formik';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';

// Icons
import {
    X,
    User,
    Phone,
    Mail,
    Shield,
    LogOut,
    Lock,
    KeyRound,
    Eye,
    EyeOff,
    Copy,
    Globe,
    Plus,
    Trash2,
    RefreshCw,
    QrCode,
    ShieldCheck,
    ShieldAlert
} from 'lucide-react';

// Project imports
import MainCard from 'ui-component/cards/MainCard';
import ModalC from 'views/dashboard/components/Modal';
import { logOutUser } from 'store/authAction';
import { changeUserPassword } from 'api/api.auth';
import { getApiKey } from 'store/ServiceAction';
import { resetApiKeys } from 'api/api.services';
import { SET_API_KEYS } from 'store/constant';

export const initialChangePasswordValue = {
    oldPassword: '',
    newPassword: '',
    confirmNewPassword: ''
};

export const changeValidationSchema = yup.object().shape({
    oldPassword: yup.string().required('Old password is required'),
    newPassword: yup.string().required('New password is required'),
    confirmNewPassword: yup.string().required('Confirm new password is required')
});

const StyledPaperCard = styled(Paper)(({ theme }) => ({
    backgroundColor: '#FFFFFF',
    borderRadius: '16px',
    padding: theme.spacing(3),
    border: '1px solid #E2E8F0',
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.02), 0 1px 2px -1px rgba(0, 0, 0, 0.02)',
    marginBottom: theme.spacing(3)
}));

const InfoRow = ({ icon: Icon, label, value }) => (
    <Stack direction="row" alignItems="center" spacing={2} py={1.8}>
        <Box
            sx={{
                width: 42,
                height: 42,
                borderRadius: '12px',
                backgroundColor: '#F0FBFC',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#00A4A8'
            }}
        >
            <Icon size={20} />
        </Box>
        <Box flex={1}>
            <Typography sx={{ fontSize: 13, color: '#64748B', fontWeight: 600, mb: 0.2 }}>{label}</Typography>
            <Typography sx={{ fontSize: 15, color: '#0F172A', fontWeight: 700, wordBreak: 'break-word' }}>
                {value || 'Not provided'}
            </Typography>
        </Box>
    </Stack>
);

InfoRow.propTypes = {
    icon: PropTypes.elementType.isRequired,
    label: PropTypes.string.isRequired,
    value: PropTypes.string
};

const Profile = () => {
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.auth || {});
    const { apiKeys } = useSelector((state) => state.service || {});

    // Active tab state: 0 = Profile & Security, 1 = API & Integrations
    const [activeTab, setActiveTab] = useState(0);

    // Change Password Modal
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [passwordLoading, setPasswordLoading] = useState(false);

    // 2FA Google Authenticator state
    const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
    const [show2FaModal, setShow2FaModal] = useState(false);
    const [authenticatorCode, setAuthenticatorCode] = useState('');
    const [twoFaLoading, setTwoFaLoading] = useState(false);
    const secretKey = 'JBSWY3DPEHPK3PXP'; // Standard TOTP secret placeholder

    // API Key State
    const [showApiKey, setShowApiKey] = useState(false);
    const [apiKeyLoading, setApiKeyLoading] = useState(false);

    // Webhook URL State
    const [webhookUrl, setWebhookUrl] = useState('https://yourdomain.com/api/webhook');
    const [webhookLoading, setWebhookLoading] = useState(false);

    // IP Whitelist State
    const [ipList, setIpList] = useState(['192.168.1.100', '102.89.23.4']);
    const [newIp, setNewIp] = useState('');

    useEffect(() => {
        dispatch(getApiKey());
    }, [dispatch]);

    // Handlers
    const togglePasswordModal = () => setShowPasswordModal((prev) => !prev);
    const toggle2FaModal = () => setShow2FaModal((prev) => !prev);

    const handleLogOut = () => {
        dispatch(logOutUser());
    };

    const changePassword = async (data) => {
        if (data.confirmNewPassword !== data.newPassword) {
            toast.error('New password and confirm password do not match');
            return;
        }
        setPasswordLoading(true);

        const response = await changeUserPassword({
            oldPassword: data.oldPassword,
            newPassword: data.newPassword
        });

        if (response.ok) {
            toast.success(response.data?.message || 'Password updated successfully');
            togglePasswordModal();
            setPasswordLoading(false);
            return;
        }

        toast.error(response.data ? response.data.message : response.problem || 'Unable to update password');
        setPasswordLoading(false);
    };

    const handleResetApiKey = async () => {
        setApiKeyLoading(true);
        try {
            const request = await resetApiKeys();
            if (request.ok) {
                toast.success('API Key reset successfully!');
                dispatch({ type: SET_API_KEYS, payload: request.data?.data });
            } else {
                toast.error(request.data?.message || 'Failed to reset API key');
            }
        } catch (error) {
            toast.error('Unable to complete API key reset');
        } finally {
            setApiKeyLoading(false);
        }
    };

    const handleCopy = (text, label) => {
        if (!text) return;
        navigator.clipboard
            .writeText(text)
            .then(() => toast.success(`${label} copied to clipboard!`))
            .catch(() => toast.error('Failed to copy'));
    };

    const handleEnable2FA = () => {
        if (authenticatorCode.length !== 6) {
            toast.error('Please enter a valid 6-digit authentication code');
            return;
        }
        setTwoFaLoading(true);
        setTimeout(() => {
            setTwoFactorEnabled(true);
            setTwoFaLoading(false);
            setShow2FaModal(false);
            setAuthenticatorCode('');
            toast.success('Google Authenticator 2FA enabled successfully!');
        }, 1200);
    };

    const handleDisable2FA = () => {
        setTwoFactorEnabled(false);
        toast.info('Google Authenticator 2FA disabled');
    };

    const handleSaveWebhook = () => {
        if (!webhookUrl || !webhookUrl.startsWith('http')) {
            toast.error('Please enter a valid URL (starting with http:// or https://)');
            return;
        }
        setWebhookLoading(true);
        setTimeout(() => {
            setWebhookLoading(false);
            toast.success('Webhook URL saved successfully!');
        }, 800);
    };

    const handleAddIp = () => {
        if (!newIp.trim()) return;
        const ipRegex = /^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$/;
        if (!ipRegex.test(newIp.trim())) {
            toast.error('Please enter a valid IPv4 address (e.g. 192.168.1.1)');
            return;
        }
        if (ipList.includes(newIp.trim())) {
            toast.info('IP address is already whitelisted');
            return;
        }
        setIpList((prev) => [...prev, newIp.trim()]);
        setNewIp('');
        toast.success('IP address added to whitelist!');
    };

    const handleRemoveIp = (ipToRemove) => {
        setIpList((prev) => prev.filter((ip) => ip !== ipToRemove));
        toast.info(`Removed ${ipToRemove} from whitelist`);
    };

    return (
        <>
            {/* Change Password Modal */}
            <ModalC isOpen={showPasswordModal} setOpen={togglePasswordModal}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                    <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#0F172A' }}>Change Password</Typography>
                    <IconButton onClick={togglePasswordModal} size="small" sx={{ color: '#64748B' }}>
                        <X size={20} />
                    </IconButton>
                </Stack>
                <Typography sx={{ fontSize: 14, color: '#64748B', mb: 3 }}>
                    Please enter your current password and choose a new password.
                </Typography>
                <Formik
                    validationSchema={changeValidationSchema}
                    initialValues={initialChangePasswordValue}
                    onSubmit={(values) => changePassword(values)}
                >
                    {({ errors, values, handleBlur, handleChange, handleSubmit, touched }) => (
                        <Box component="form" noValidate>
                            <Stack spacing={2.5}>
                                <TextField
                                    fullWidth
                                    id="oldPassword"
                                    label="Current Password"
                                    name="oldPassword"
                                    type="password"
                                    variant="outlined"
                                    value={values.oldPassword}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={touched.oldPassword && Boolean(errors.oldPassword)}
                                    helperText={touched.oldPassword && errors.oldPassword}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Lock size={18} color="#94A3B8" />
                                            </InputAdornment>
                                        )
                                    }}
                                />

                                <TextField
                                    fullWidth
                                    id="newPassword"
                                    label="New Password"
                                    name="newPassword"
                                    type="password"
                                    variant="outlined"
                                    value={values.newPassword}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={touched.newPassword && Boolean(errors.newPassword)}
                                    helperText={touched.newPassword && errors.newPassword}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Shield size={18} color="#94A3B8" />
                                            </InputAdornment>
                                        )
                                    }}
                                />

                                <TextField
                                    fullWidth
                                    id="confirmNewPassword"
                                    label="Confirm New Password"
                                    name="confirmNewPassword"
                                    type="password"
                                    variant="outlined"
                                    value={values.confirmNewPassword}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={touched.confirmNewPassword && Boolean(errors.confirmNewPassword)}
                                    helperText={touched.confirmNewPassword && errors.confirmNewPassword}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Shield size={18} color="#94A3B8" />
                                            </InputAdornment>
                                        )
                                    }}
                                />
                            </Stack>
                            <LoadingButton
                                loading={passwordLoading}
                                onClick={handleSubmit}
                                variant="contained"
                                fullWidth
                                sx={{
                                    mt: 4,
                                    py: 1.5,
                                    borderRadius: 3,
                                    fontSize: 15,
                                    fontWeight: 700,
                                    backgroundColor: '#00A4A8',
                                    boxShadow: 'none',
                                    textTransform: 'none',
                                    '&:hover': {
                                        backgroundColor: '#008B8E',
                                        boxShadow: 'none'
                                    }
                                }}
                            >
                                Update Password
                            </LoadingButton>
                        </Box>
                    )}
                </Formik>
            </ModalC>

            {/* Google Authenticator 2FA Modal */}
            <ModalC isOpen={show2FaModal} setOpen={toggle2FaModal}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                    <Typography sx={{ fontSize: 20, fontWeight: 800, color: '#0F172A' }}>Setup Google Authenticator</Typography>
                    <IconButton onClick={toggle2FaModal} size="small" sx={{ color: '#64748B' }}>
                        <X size={20} />
                    </IconButton>
                </Stack>
                <Typography sx={{ fontSize: 14, color: '#64748B', mb: 3 }}>
                    Scan the QR code with your Google Authenticator app or manually enter the secret key.
                </Typography>

                <Stack spacing={3} alignItems="center">
                    {/* QR Code Placeholder */}
                    <Box
                        sx={{
                            p: 2,
                            borderRadius: '16px',
                            backgroundColor: '#FFFFFF',
                            border: '2px dashed #CBD5E1',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 180,
                            height: 180
                        }}
                    >
                        <img
                            src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=otpauth://totp/DataApp:${
                                user?.email || 'user'
                            }?secret=${secretKey}&issuer=DataApp`}
                            alt="2FA QR Code"
                            style={{ width: 140, height: 140 }}
                        />
                    </Box>

                    {/* Secret Key Box */}
                    <Paper
                        elevation={0}
                        sx={{
                            p: 1.5,
                            width: '100%',
                            backgroundColor: '#F8FAFC',
                            border: '1px solid #E2E8F0',
                            borderRadius: '12px'
                        }}
                    >
                        <Typography sx={{ fontSize: 12, color: '#64748B', fontWeight: 600, mb: 0.5 }}>SECRET KEY</Typography>
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Typography sx={{ fontFamily: 'monospace', fontWeight: 800, color: '#0F172A', fontSize: 16 }}>
                                {secretKey}
                            </Typography>
                            <Button
                                size="small"
                                startIcon={<Copy size={14} />}
                                onClick={() => handleCopy(secretKey, 'Secret Key')}
                                sx={{ textTransform: 'none', fontWeight: 700 }}
                            >
                                Copy
                            </Button>
                        </Stack>
                    </Paper>

                    {/* Code Verification Field */}
                    <Box sx={{ width: '100%' }}>
                        <Typography sx={{ fontSize: 13, color: '#0F172A', fontWeight: 700, mb: 1 }}>
                            Enter 6-Digit Authenticator Code
                        </Typography>
                        <TextField
                            fullWidth
                            placeholder="000000"
                            value={authenticatorCode}
                            onChange={(e) => setAuthenticatorCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
                            inputProps={{
                                maxLength: 6,
                                style: { textAlign: 'center', letterSpacing: '0.4em', fontSize: '1.25rem', fontWeight: 800 }
                            }}
                        />
                    </Box>

                    <LoadingButton
                        loading={twoFaLoading}
                        onClick={handleEnable2FA}
                        variant="contained"
                        fullWidth
                        sx={{
                            py: 1.5,
                            borderRadius: 3,
                            fontSize: 15,
                            fontWeight: 700,
                            backgroundColor: '#00A4A8',
                            boxShadow: 'none',
                            textTransform: 'none',
                            '&:hover': {
                                backgroundColor: '#008B8E',
                                boxShadow: 'none'
                            }
                        }}
                    >
                        Verify & Enable 2FA
                    </LoadingButton>
                </Stack>
            </ModalC>

            {/* Main Profile & Settings Layout */}
            <MainCard content={false}>
                <Box sx={{ backgroundColor: '#F8FAFC', minHeight: '100vh', py: { xs: 2, md: 4 }, px: { xs: 1.5, sm: 2.5, md: 4 } }}>
                    <Box sx={{ maxWidth: 860, mx: 'auto' }}>
                        {/* User Header Profile Card */}
                        <Card elevation={0} sx={{ borderRadius: 4, overflow: 'hidden', border: '1px solid #E2E8F0', mb: 3 }}>
                            <Box sx={{ height: 110, backgroundColor: '#00A4A8', position: 'relative' }} />
                            <Box sx={{ px: 3, pb: 3, mt: -5, position: 'relative' }}>
                                <Stack
                                    direction={{ xs: 'column', sm: 'row' }}
                                    alignItems={{ xs: 'flex-start', sm: 'flex-end' }}
                                    spacing={2}
                                >
                                    <Avatar
                                        src={'https://www.pngall.com/wp-content/uploads/12/Avatar-Profile-PNG-Photos.png'}
                                        sx={{
                                            height: 96,
                                            width: 96,
                                            border: '4px solid #FFF',
                                            backgroundColor: '#FFF',
                                            boxShadow: '0 4px 14px 0 rgba(0,0,0,0.06)'
                                        }}
                                    >
                                        {user?.firstName?.charAt(0)}
                                    </Avatar>
                                    <Box flex={1} sx={{ mt: { xs: 1, sm: 0 } }}>
                                        <Typography sx={{ fontSize: 24, fontWeight: 800, color: '#0F172A', mb: 0.2 }}>
                                            {user ? `${user.firstName} ${user.lastName}` : 'User Profile'}
                                        </Typography>
                                        <Typography sx={{ fontSize: 14, color: '#64748B', fontWeight: 600 }}>@{user?.username}</Typography>
                                    </Box>

                                    <Button
                                        variant="contained"
                                        color="error"
                                        onClick={handleLogOut}
                                        startIcon={<LogOut size={16} />}
                                        sx={{
                                            borderRadius: 2.5,
                                            fontWeight: 700,
                                            backgroundColor: '#FEE2E2',
                                            color: '#EF4444',
                                            boxShadow: 'none',
                                            textTransform: 'none',
                                            '&:hover': { backgroundColor: '#FECACA', boxShadow: 'none' }
                                        }}
                                    >
                                        Log Out
                                    </Button>
                                </Stack>
                            </Box>
                        </Card>

                        {/* Navigation Tabs */}
                        <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid #E2E8F0', mb: 3, overflow: 'hidden' }}>
                            <Tabs
                                value={activeTab}
                                onChange={(e, val) => setActiveTab(val)}
                                variant="fullWidth"
                                sx={{
                                    '& .MuiTab-root': {
                                        textTransform: 'none',
                                        fontWeight: 700,
                                        fontSize: '0.95rem',
                                        py: 2
                                    },
                                    '& .Mui-selected': {
                                        color: '#00A4A8'
                                    },
                                    '& .MuiTabs-indicator': {
                                        backgroundColor: '#00A4A8',
                                        height: 3
                                    }
                                }}
                            >
                                <Tab icon={<User size={18} />} iconPosition="start" label="Profile & Security" />
                                <Tab icon={<KeyRound size={18} />} iconPosition="start" label="API & Integrations" />
                            </Tabs>
                        </Paper>

                        {/* TAB 0: PROFILE & SECURITY */}
                        {activeTab === 0 && (
                            <Stack spacing={3}>
                                {/* Account Information Card */}
                                <StyledPaperCard elevation={0}>
                                    <Typography sx={{ fontSize: 18, fontWeight: 800, color: '#0F172A', mb: 1 }}>
                                        Personal Information
                                    </Typography>
                                    <Typography sx={{ fontSize: 14, color: '#64748B', mb: 2 }}>
                                        Your account details and contact information.
                                    </Typography>
                                    <Divider sx={{ mb: 1 }} />

                                    <InfoRow icon={User} label="Full Name" value={user ? `${user.firstName} ${user.lastName}` : ''} />
                                    <Divider sx={{ borderStyle: 'dashed' }} />
                                    <InfoRow icon={Phone} label="Phone Number" value={user?.phone} />
                                    <Divider sx={{ borderStyle: 'dashed' }} />
                                    <InfoRow icon={Mail} label="Email Address" value={user?.email} />
                                </StyledPaperCard>

                                {/* Password & Security Settings Card */}
                                <StyledPaperCard elevation={0}>
                                    <Typography sx={{ fontSize: 18, fontWeight: 800, color: '#0F172A', mb: 1 }}>
                                        Security Settings
                                    </Typography>
                                    <Typography sx={{ fontSize: 14, color: '#64748B', mb: 3 }}>
                                        Manage your account password and multi-factor authentication.
                                    </Typography>

                                    {/* Change Password Row */}
                                    <Stack
                                        direction={{ xs: 'column', sm: 'row' }}
                                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                                        justifyContent="space-between"
                                        spacing={2}
                                        pb={3}
                                    >
                                        <Box>
                                            <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#0F172A' }}>Password</Typography>
                                            <Typography sx={{ fontSize: 13, color: '#64748B' }}>
                                                Update your account login password periodically.
                                            </Typography>
                                        </Box>
                                        <Button
                                            variant="outlined"
                                            onClick={togglePasswordModal}
                                            startIcon={<KeyRound size={16} />}
                                            sx={{
                                                borderRadius: 2.5,
                                                fontWeight: 700,
                                                borderColor: '#CBD5E1',
                                                color: '#334155',
                                                textTransform: 'none',
                                                '&:hover': { borderColor: '#00A4A8', color: '#00A4A8', backgroundColor: '#F0FBFC' }
                                            }}
                                        >
                                            Change Password
                                        </Button>
                                    </Stack>

                                    <Divider sx={{ mb: 3 }} />

                                    {/* 2FA Google Authenticator Row */}
                                    <Stack
                                        direction={{ xs: 'column', sm: 'row' }}
                                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                                        justifyContent="space-between"
                                        spacing={2}
                                    >
                                        <Box>
                                            <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
                                                <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#0F172A' }}>
                                                    Google Authenticator (2FA)
                                                </Typography>
                                                <Chip
                                                    icon={twoFactorEnabled ? <ShieldCheck size={14} /> : <ShieldAlert size={14} />}
                                                    label={twoFactorEnabled ? 'Enabled' : 'Disabled'}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: twoFactorEnabled ? '#DCFCE7' : '#FEF3C7',
                                                        color: twoFactorEnabled ? '#15803D' : '#D97706',
                                                        fontWeight: 700,
                                                        fontSize: '0.75rem'
                                                    }}
                                                />
                                            </Stack>
                                            <Typography sx={{ fontSize: 13, color: '#64748B' }}>
                                                Protect your recharges and account with 6-digit TOTP verification codes.
                                            </Typography>
                                        </Box>

                                        {twoFactorEnabled ? (
                                            <Button
                                                variant="outlined"
                                                color="error"
                                                onClick={handleDisable2FA}
                                                sx={{ borderRadius: 2.5, fontWeight: 700, textTransform: 'none' }}
                                            >
                                                Disable 2FA
                                            </Button>
                                        ) : (
                                            <Button
                                                variant="contained"
                                                onClick={toggle2FaModal}
                                                startIcon={<QrCode size={16} />}
                                                sx={{
                                                    borderRadius: 2.5,
                                                    fontWeight: 700,
                                                    backgroundColor: '#00A4A8',
                                                    textTransform: 'none',
                                                    '&:hover': { backgroundColor: '#008B8E' }
                                                }}
                                            >
                                                Setup 2FA
                                            </Button>
                                        )}
                                    </Stack>
                                </StyledPaperCard>
                            </Stack>
                        )}

                        {/* TAB 1: API & INTEGRATIONS */}
                        {activeTab === 1 && (
                            <Stack spacing={3}>
                                {/* Live API Key & Reset View Card */}
                                <StyledPaperCard elevation={0}>
                                    <Typography sx={{ fontSize: 18, fontWeight: 800, color: '#0F172A', mb: 0.5 }}>
                                        API Key Management
                                    </Typography>
                                    <Typography sx={{ fontSize: 14, color: '#64748B', mb: 3 }}>
                                        Use your bearer API key to authenticate RESTful requests to our VTU endpoints.
                                    </Typography>

                                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0F172A', mb: 1 }}>
                                        Live Secret API Key
                                    </Typography>
                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} mb={3}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            type={showApiKey ? 'text' : 'password'}
                                            value={apiKeys?.apiKey || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...'}
                                            InputProps={{
                                                readOnly: true,
                                                sx: { fontFamily: 'monospace', borderRadius: '10px', backgroundColor: '#F8FAFC' },
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton size="small" onClick={() => setShowApiKey((prev) => !prev)}>
                                                            {showApiKey ? <EyeOff size={18} /> : <Eye size={18} />}
                                                        </IconButton>
                                                    </InputAdornment>
                                                )
                                            }}
                                        />
                                        <Button
                                            variant="outlined"
                                            startIcon={<Copy size={16} />}
                                            onClick={() => handleCopy(apiKeys?.apiKey, 'API Key')}
                                            sx={{
                                                minWidth: 100,
                                                borderRadius: '10px',
                                                fontWeight: 700,
                                                textTransform: 'none',
                                                borderColor: '#CBD5E1',
                                                color: '#334155'
                                            }}
                                        >
                                            Copy
                                        </Button>
                                    </Stack>

                                    <LoadingButton
                                        loading={apiKeyLoading}
                                        onClick={handleResetApiKey}
                                        variant="contained"
                                        color="error"
                                        startIcon={<RefreshCw size={16} />}
                                        sx={{
                                            borderRadius: '10px',
                                            fontWeight: 700,
                                            textTransform: 'none',
                                            boxShadow: 'none'
                                        }}
                                    >
                                        Reset Secret API Key
                                    </LoadingButton>
                                </StyledPaperCard>

                                {/* Webhook Configuration Card */}
                                <StyledPaperCard elevation={0}>
                                    <Typography sx={{ fontSize: 18, fontWeight: 800, color: '#0F172A', mb: 0.5 }}>
                                        Webhook Notification URL
                                    </Typography>
                                    <Typography sx={{ fontSize: 14, color: '#64748B', mb: 3 }}>
                                        Provide an HTTP POST endpoint to receive real-time updates when transaction statuses change.
                                    </Typography>

                                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0F172A', mb: 1 }}>
                                        Callback Endpoint URL
                                    </Typography>
                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            placeholder="https://yourdomain.com/api/vtu/webhook"
                                            value={webhookUrl}
                                            onChange={(e) => setWebhookUrl(e.target.value)}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <Globe size={16} color="#64748B" />
                                                    </InputAdornment>
                                                ),
                                                sx: { borderRadius: '10px' }
                                            }}
                                        />
                                        <LoadingButton
                                            loading={webhookLoading}
                                            onClick={handleSaveWebhook}
                                            variant="contained"
                                            sx={{
                                                minWidth: 120,
                                                borderRadius: '10px',
                                                fontWeight: 700,
                                                backgroundColor: '#00A4A8',
                                                textTransform: 'none',
                                                '&:hover': { backgroundColor: '#008B8E' }
                                            }}
                                        >
                                            Save URL
                                        </LoadingButton>
                                    </Stack>
                                </StyledPaperCard>

                                {/* IP Whitelist Management Card */}
                                <StyledPaperCard elevation={0}>
                                    <Typography sx={{ fontSize: 18, fontWeight: 800, color: '#0F172A', mb: 0.5 }}>
                                        IP Address Whitelist
                                    </Typography>
                                    <Typography sx={{ fontSize: 14, color: '#64748B', mb: 3 }}>
                                        Restrict API access to specific server IP addresses for maximum security.
                                    </Typography>

                                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0F172A', mb: 1 }}>
                                        Add IPv4 Address
                                    </Typography>
                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} mb={3}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            placeholder="e.g. 192.168.1.1"
                                            value={newIp}
                                            onChange={(e) => setNewIp(e.target.value)}
                                            InputProps={{ sx: { borderRadius: '10px', fontFamily: 'monospace' } }}
                                        />
                                        <Button
                                            variant="contained"
                                            startIcon={<Plus size={16} />}
                                            onClick={handleAddIp}
                                            sx={{
                                                minWidth: 110,
                                                borderRadius: '10px',
                                                fontWeight: 700,
                                                backgroundColor: '#00A4A8',
                                                textTransform: 'none',
                                                '&:hover': { backgroundColor: '#008B8E' }
                                            }}
                                        >
                                            Add IP
                                        </Button>
                                    </Stack>

                                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0F172A', mb: 1.5 }}>
                                        Currently Whitelisted IPs ({ipList.length})
                                    </Typography>
                                    <Stack direction="row" flexWrap="wrap" gap={1.5}>
                                        {ipList.length > 0 ? (
                                            ipList.map((ip) => (
                                                <Chip
                                                    key={ip}
                                                    label={ip}
                                                    onDelete={() => handleRemoveIp(ip)}
                                                    deleteIcon={<Trash2 size={14} />}
                                                    variant="outlined"
                                                    sx={{
                                                        fontFamily: 'monospace',
                                                        fontWeight: 700,
                                                        fontSize: '0.85rem',
                                                        borderRadius: '8px',
                                                        borderColor: '#CBD5E1',
                                                        py: 2
                                                    }}
                                                />
                                            ))
                                        ) : (
                                            <Typography sx={{ fontSize: 13, color: '#94A3B8', fontStyle: 'italic' }}>
                                                No IPs whitelisted. API requests allowed from any IP.
                                            </Typography>
                                        )}
                                    </Stack>
                                </StyledPaperCard>
                            </Stack>
                        )}
                    </Box>
                </Box>
            </MainCard>
        </>
    );
};

export default Profile;
